import CountUp from "react-countup";
import PageHeader from "@/components/PageHeader";
import { STATS, FOUNDER, FOUNDER_SRC, ABOUT_PILLARS, LOGO_SRC } from "@/constants/site";
import SEO, { breadcrumb } from "@/components/SEO";

export default function About() {
  return (
    <div>
      <SEO
        title="About AM Group of Education — Coaching, Mentorship & Student Development"
        description="AM Group of Education is a premium coaching institute committed to quality education, personalized mentorship, career guidance and holistic student development."
        path="/about"
        jsonLd={[
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "About Us", path: "/about" },
          ]),
        ]}
      />
      <PageHeader title="About AM Group of Education" subtitle="A modern coaching institute with old-school discipline and new-age tools — built to help every learner lead tomorrow." crumb="About" />

      {/* OUR STORY — content-focused, no founder image here */}
      <section className="relative py-20 lg:py-28 bg-white overflow-hidden">
        <img
          src={LOGO_SRC}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-32 top-1/2 -translate-y-1/2 w-[520px] h-auto opacity-[0.04] object-contain select-none hidden lg:block"
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="kicker mx-auto">Our Story</span>
          <h2 className="display-serif mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-brand-dark leading-[1.05] tracking-tight" data-aos="fade-up">
            Where Mentorship Meets <span className="text-brand-primary">Mastery</span>.
          </h2>
          <div className="mt-10 max-w-3xl mx-auto space-y-6 text-left sm:text-center text-brand-mute leading-relaxed text-[15px] sm:text-base" data-aos="fade-up" data-aos-delay="100">
            <p>
              AM Group of Education was founded with a simple yet powerful vision — to provide quality education that transforms lives. We believe every student deserves not only exceptional teaching but also genuine mentorship that builds confidence, discipline, and lifelong success.
            </p>
            <p>
              Today, we proudly offer premium coaching for <strong className="text-brand-dark font-semibold">School Education (Classes 1–12)</strong>, <strong className="text-brand-dark font-semibold">Competitive Examinations</strong>, <strong className="text-brand-dark font-semibold">Language Courses</strong>, and <strong className="text-brand-dark font-semibold">IT Skills</strong> — all under one roof. Our learning approach combines experienced faculty, modern teaching methods, practical learning, and personalized guidance to help every student achieve their academic and career goals.
            </p>
            <p>
              Whether preparing for board examinations, competitive exams, or professional skill development, AM Group of Education is committed to creating future leaders through excellence in education.
            </p>
          </div>
        </div>

        {/* Pillars */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ABOUT_PILLARS.map((p, i) => (
              <div key={p.title} className="lift rounded-2xl border border-brand-line bg-brand-cream p-6" data-aos="fade-up" data-aos-delay={i*50} data-testid={`about-pillar-${i}`}>
                <span className="w-12 h-12 rounded-xl bg-brand-primary text-brand-gold grid place-items-center">
                  <i className={`fa-solid fa-${p.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-semibold text-brand-dark">{p.title}</h3>
                <p className="mt-2 text-sm text-brand-mute leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-14 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="rounded-2xl bg-white border border-brand-line p-6 text-center">
                <i className={`fa-solid fa-${s.icon} text-brand-gold text-2xl`} />
                <div className="display-serif text-3xl font-extrabold text-brand-dark mt-2">
                  <CountUp end={s.value} duration={2.6} suffix={s.suffix} separator="," enableScrollSpy scrollSpyOnce />
                </div>
                <div className="text-xs uppercase tracking-widest text-brand-mute mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* MEET OUR FOUNDER & CEO — full uncropped portrait, CEO layout   */}
      {/* ============================================================ */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-b from-white to-brand-cream overflow-hidden" data-testid="founder-section">
        {/* Watermark */}
        <img
          src={LOGO_SRC}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -left-40 bottom-0 w-[560px] h-auto opacity-[0.035] object-contain select-none hidden lg:block"
        />
        {/* Decorative gold accent */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-brand-gold/10 blur-[120px] pointer-events-none" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section heading */}
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="kicker mx-auto">Leadership</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark">Meet Our Founder &amp; CEO</h2>
          </div>

          {/* HERO: photo + name/designation side by side */}
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Full uncropped portrait — luxury CEO card */}
            <div className="lg:col-span-6 order-1 lg:order-1" data-aos="fade-right">
              <div className="relative mx-auto max-w-lg group">
                {/* Beautiful radial background behind card */}
                <div className="absolute -inset-10 -z-10 rounded-[48px] bg-[radial-gradient(circle_at_30%_30%,rgba(212,175,55,.35),transparent_60%),radial-gradient(circle_at_70%_80%,rgba(111,78,55,.25),transparent_60%)] blur-2xl" aria-hidden />

                {/* Outer luxury gradient border ring */}
                <div className="relative rounded-[32px] p-[3px] bg-gradient-to-br from-brand-gold via-brand-primary to-brand-dark shadow-[0_40px_100px_-30px_rgba(78,52,46,.55)] transition-transform duration-500 group-hover:-translate-y-2 group-hover:shadow-[0_50px_120px_-30px_rgba(212,175,55,.5)]">
                  {/* Thin inner glow outline */}
                  <div className="rounded-[29px] p-[2px] bg-gradient-to-tl from-brand-gold/40 via-transparent to-brand-gold/40">
                    <div className="relative rounded-[27px] overflow-hidden bg-brand-cream">
                      <img
                        src={FOUNDER_SRC}
                        alt={`${FOUNDER.name} — Founder & CEO of AM Group of Education`}
                        className="w-full h-auto object-cover block select-none transition-transform duration-700 group-hover:scale-[1.02]"
                        style={{ imageRendering: "auto", filter: "contrast(1.08) saturate(1.1) brightness(1.03)" }}
                        draggable={false}
                        data-testid="founder-photo"
                      />
                      {/* Subtle inner vignette for premium feel */}
                      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_0%,transparent_55%,rgba(42,30,24,.25)_100%)]" />
                      {/* Corner brand chip */}
                      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md border border-brand-gold/50 rounded-full pl-1 pr-3 py-1 flex items-center gap-2 shadow-md">
                        <img src={LOGO_SRC} alt="" className="w-6 h-6 rounded-full object-contain" />
                        <span className="text-[10px] font-semibold text-brand-primary uppercase tracking-widest">AM Group</span>
                      </div>
                      {/* Golden signature bar */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
                    </div>
                  </div>
                </div>

                {/* Signature line beneath card */}
                <div className="mt-6 flex items-center gap-3 justify-center">
                  <img src={LOGO_SRC} alt="AM Group" className="h-12 w-auto object-contain" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-primary font-semibold">AM Group of Education</div>
                    <div className="text-sm text-brand-mute">Learn Today, Lead Tomorrow</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right-side name / title / summary */}
            <div className="lg:col-span-6 order-2 lg:order-2" data-aos="fade-left">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-brand-primary bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5">
                <i className="fa-solid fa-crown text-brand-gold" /> Founder &amp; CEO
              </div>
              <h3 className="display-serif mt-5 text-5xl sm:text-6xl lg:text-7xl font-extrabold text-brand-dark leading-[0.98] tracking-tight">
                {FOUNDER.name}
              </h3>
              <div className="mt-4 flex items-center gap-3">
                <span className="w-12 h-0.5 bg-brand-gold" />
                <span className="text-sm uppercase tracking-[0.25em] font-semibold text-brand-primary">
                  {FOUNDER.role}
                </span>
              </div>

              <div className="mt-8 space-y-5 text-brand-mute leading-relaxed text-[15px] sm:text-base">
                {FOUNDER.intro.map((p, i) => (
                  <p key={i} className={i === 0 ? "text-lg text-brand-ink" : ""}>{p}</p>
                ))}
              </div>

              {/* Quick facts */}
              <div className="mt-8 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white border border-brand-line p-4">
                  <div className="text-[10px] uppercase tracking-widest text-brand-primary font-semibold">UPSC CSE</div>
                  <div className="mt-1 flex items-center gap-2 font-semibold text-brand-dark">
                    <i className="fa-solid fa-landmark text-brand-gold" /> Interview · 2×
                  </div>
                </div>
                <div className="rounded-2xl bg-white border border-brand-line p-4">
                  <div className="text-[10px] uppercase tracking-widest text-brand-primary font-semibold">Qualifications</div>
                  <div className="mt-1 flex items-center gap-2 font-semibold text-brand-dark">
                    <i className="fa-solid fa-graduation-cap text-brand-gold" /> 5 Masters &amp; PG
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Academic Qualifications */}
          <div className="mt-20 rounded-3xl bg-white border border-brand-line p-8 sm:p-10 shadow-[0_25px_60px_-30px_rgba(78,52,46,.25)]" data-aos="fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-12 rounded-xl bg-brand-primary text-brand-gold grid place-items-center"><i className="fa-solid fa-graduation-cap text-lg" /></span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-brand-primary font-semibold">Academic</div>
                <h3 className="display-serif text-2xl font-bold text-brand-dark">Qualifications</h3>
              </div>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3">
              {FOUNDER.qualifications.map((q) => (
                <li key={q.text} className="flex items-start gap-3 bg-brand-cream border border-brand-line rounded-xl px-4 py-3">
                  <span className="text-2xl leading-none" aria-hidden>{q.icon}</span>
                  <span className="text-sm text-brand-ink leading-relaxed pt-0.5">{q.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Civil Services */}
          <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start" data-aos="fade-up">
            <div className="lg:col-span-5">
              <span className="kicker">Civil Services Journey</span>
              <h3 className="display-serif mt-3 text-2xl sm:text-3xl font-bold text-brand-dark">Twice UPSC Interview Appeared</h3>
              <div className="mt-4 inline-flex items-center gap-3 rounded-2xl bg-brand-dark text-white px-5 py-4 shadow-lg">
                <span className="w-10 h-10 rounded-xl bg-brand-gold text-brand-dark grid place-items-center"><i className="fa-solid fa-landmark" /></span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-gold font-semibold">UPSC CSE</div>
                  <div className="font-semibold">Interview Appeared · 2×</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 space-y-4 text-brand-mute leading-relaxed">
              {FOUNDER.civil.map((p, i) => (<p key={i}>{p}</p>))}
            </div>
          </div>

          {/* Leadership Philosophy quote */}
          <div className="mt-14 relative rounded-3xl overflow-hidden bg-brand-dark text-white p-10 sm:p-14 border border-brand-gold/40" data-aos="zoom-in-up">
            <i className="fa-solid fa-quote-left absolute top-6 left-6 text-brand-gold/25 text-7xl" />
            <i className="fa-solid fa-quote-right absolute bottom-6 right-6 text-brand-gold/25 text-7xl" />
            <div className="relative text-center max-w-3xl mx-auto">
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-gold">Leadership Philosophy</div>
              <p className="display-serif mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug">
                &ldquo;{FOUNDER.philosophy}&rdquo;
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <span className="w-10 h-px bg-brand-gold" />
                <span className="text-xs uppercase tracking-[0.3em] font-semibold text-brand-gold">{FOUNDER.name}</span>
                <span className="w-10 h-px bg-brand-gold" />
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="mt-14 rounded-3xl border border-brand-line bg-white p-8 sm:p-10 shadow-[0_25px_60px_-30px_rgba(78,52,46,.25)]" data-aos="fade-up">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-12 rounded-xl bg-brand-primary text-brand-gold grid place-items-center"><i className="fa-solid fa-compass text-lg" /></span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-brand-primary font-semibold">Our Direction</div>
                <h3 className="display-serif text-2xl font-bold text-brand-dark">Vision for AM Group of Education</h3>
              </div>
            </div>
            <div className="space-y-4 text-brand-mute leading-relaxed">
              {FOUNDER.vision.map((p, i) => (<p key={i}>{p}</p>))}
            </div>
          </div>

          {/* Tagline */}
          <div className="mt-14 text-center" data-aos="fade-up" data-testid="founder-tagline">
            <div className="display-serif text-2xl sm:text-3xl lg:text-4xl font-extrabold bg-gradient-to-r from-brand-primary via-brand-gold to-brand-primary bg-clip-text text-transparent">
              &ldquo;{FOUNDER.tagline}&rdquo;
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
